import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { ActivityIndicator, ScrollView, useWindowDimensions } from 'react-native';
import { Paragraph, View, XStack, YStack } from 'tamagui';

import { Text } from '@/components/text';
import { AuthenticatedImage } from '@/components/authenticated-image';
import { Badge } from '@/components/badge';
import { SolidSurface } from '@/components/glass-surface';
import { Heading } from '@/components/heading';
import { LoadingIndicator } from '@/components/loading-indicator';
import { PrimaryButton } from '@/components/primary-button';
import { PseudonymousAvatar } from '@/components/pseudonymous-avatar';
import { ScreenContainer } from '@/components/screen-container';
import { ScreenHeader } from '@/components/screen-header';
import { Subheading } from '@/components/subheading';
import { TextLink } from '@/components/text-link';
import { useAuth } from '@/hooks/auth-context';
import { useAllConnections } from '@/hooks/use-connections';
import { useMyRevealRequests } from '@/hooks/use-my-reveal-requests';
import { useProfile } from '@/hooks/use-profile';
import { api, ApiError, type RevealRequest } from '@/lib/api';
import { displayHandle } from '@/lib/avatar-identity';
import { queryKeys } from '@/lib/query-keys';
import { canReRequestAfterDecline, describeCooldown, describeRevealRequestStatus } from '@/lib/reveal-request-status';
import { borderWidth, space } from '@/theme/tokens.generated';

// Real screen, not a stub -- prerequisite infrastructure for issues
// #16 and #17 (see this plan's Task 5 scoping note). Every screen
// under (app) only renders once the root nav gate has already
// resolved to 'signed-up', so currentUserId
// below is always defined in practice.
export function ProfileView({ userId }: { userId: string | undefined }) {
  // Handles are long unbroken words: below ~340px step the title down so one
  // never breaks mid-word beside the avatar (issue #550).
  const { width: screenWidth } = useWindowDimensions();
  const handleSize = screenWidth < 340 ? '$titleS' : '$title';
  const router = useRouter();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const currentUserId = auth.status === 'signed-up' ? auth.user.id : undefined;
  const isOwnProfile = currentUserId !== undefined && currentUserId === userId;

  const profile = useProfile(userId as string, { enabled: userId !== undefined });

  // Needed to know whether a reveal request already exists with this
  // person (and its status), so the UI doesn't offer "Request reveal"
  // a second time on top of a pending or mutual one. fetchAllRevealRequests
  // loops through every cursor page to return every request the caller
  // is party to -- filtered client-side for the one involving this
  // specific profile's userId, since there's no backend query scoped to
  // a single pair (LatestDeclineAt/FindActive are internal-only, not
  // exposed via any GET route). Looping client-side (rather than just
  // the first page) matters here specifically: an older pending
  // request with this userId could otherwise sit past the first
  // 20-row page and be missed entirely.
  const revealRequests = useMyRevealRequests({ enabled: !isOwnProfile });

  const existingRequest = useMemo(
    () => revealRequests.data?.find((rr) => rr.requesterId === userId || rr.recipientId === userId),
    [revealRequests.data, userId],
  );

  // Issue #102: a declined request used to render as a permanent
  // "Declined" card forever, even once the backend's own cooldown
  // (reveal_request.go's declineCooldown) had elapsed and a fresh
  // POST would succeed -- describeCooldown was only ever wired up on
  // the reveal-requests list screen, never here. Only the ORIGINAL
  // REQUESTER is cooldown-gated (see canReRequestAfterDecline's own
  // doc comment), so this re-opens the button for exactly the side
  // the backend actually restricted.
  const canReRequest =
    existingRequest !== undefined && currentUserId !== undefined
      ? canReRequestAfterDecline(existingRequest, currentUserId, new Date())
      : false;

  const requestReveal = useMutation({
    mutationFn: () => api.createRevealRequest(userId as string, null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.revealRequests() });
    },
  });

  const profileNotFound = profile.isError && profile.error instanceof ApiError && profile.error.status === 404;

  return (
    <ScreenContainer>
      <YStack flex={1}>
        <YStack>
          <ScreenHeader onBack={() => router.back()} />
        </YStack>

        {profile.isPending ? (
          <View flex={1} alignItems="center" justifyContent="center">
            <LoadingIndicator />
          </View>
        ) : profileNotFound ? (
          <YStack flex={1} alignItems="center" justifyContent="center" gap="$3">
            <Subheading color="$color" textAlign="center">
              This profile is gone
            </Subheading>
            <Paragraph color="$colorSecondary" textAlign="center">
              It may have been removed.
            </Paragraph>
            {/* Issue #151 AC4b: this branch had zero way out besides the
                shared top-of-screen BackLink -- every OTHER dead-end
                branch on this same screen (the generic load-error branch
                right below) already gets its own PrimaryButton. Pushes
                to the app root rather than router.back(): "gone" means
                there's nothing to go back TO on this specific profile,
                so Threads is the more useful landing spot than
                re-showing wherever the link that led here came from. */}
            <PrimaryButton onPress={() => router.push('/')}>Back to Threads</PrimaryButton>
          </YStack>
        ) : profile.isError && !profile.data ? (
          <YStack flex={1} alignItems="center" justifyContent="center" gap="$3">
            <Subheading color="$color" textAlign="center">
              Couldn&apos;t load this profile
            </Subheading>
            <Paragraph color="$colorSecondary" textAlign="center">
              Check your connection and try again.
            </Paragraph>
            <PrimaryButton onPress={() => profile.refetch()}>Try again</PrimaryButton>
          </YStack>
        ) : profile.data ? (
          <ScrollView contentContainerStyle={{ paddingTop: space[2], paddingBottom: space[6], gap: space[5] }}>
            <XStack alignItems="center" gap="$3">
              <PseudonymousAvatar handle={profile.data.handle} id={userId as string} size={48} />
              <YStack flexShrink={1} gap="$1">
                <Heading fontSize={handleSize} lineHeight={handleSize} numberOfLines={2}>
                  {displayHandle(profile.data.handle)}
                </Heading>
                {/* Issue #176: Tier 0's badge (#175) was previously
                    visible only on the user's OWN profile -- the whole
                    point of earning it is that OTHER people see it.
                    "Real person confirmed", not "Verified" -- same
                    reserved-word distinction settings/index.tsx already
                    draws (design.md's Verification model keeps
                    "Verified" for Tier 1, which nobody can hold yet).
                    Reuses Badge's existing "success" variant -- the
                    same already-contrast-checked token pair every other
                    status badge on this screen uses, not a new color
                    choice for this one marker. */}
                {profile.data.tier0Status === 'tier0_passed' && (
                  <Badge variant="success" alignSelf="flex-start">
                    Real person confirmed
                  </Badge>
                )}
                {profile.data.distance && (
                  <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
                    {profile.data.distance}
                  </Text>
                )}
              </YStack>
            </XStack>

            {profile.data.bio !== '' && <Paragraph color="$colorSecondary">{profile.data.bio}</Paragraph>}

            {(profile.data.gender || profile.data.ageRange) && (
              <XStack gap="$2" flexWrap="wrap">
                {profile.data.gender && <Badge variant="neutral">{profile.data.gender}</Badge>}
                {profile.data.ageRange && <Badge variant="neutral">{profile.data.ageRange}</Badge>}
              </XStack>
            )}

            {/* Photos only unlock with a MUTUAL Connection and no block
                (GetProfileHandler's photo gate) -- rendering them here is
                the payoff the reveal contract promises. Photos first:
                they're the whole point of the reveal.

                AuthenticatedImage, not a plain <Image>: each entry is a
                gated /photos/{id} path (GET requires a bearer token --
                see backend/internal/http/photo.go's GetPhotoHandler), so
                a plain source={{uri}} would 401 the moment a real photo
                existed (issue #136's review caught this as a latent bug
                -- it never surfaced only because no upload path shipped
                before now). */}
            {profile.data.photos.length > 0 && (
              <YStack gap="$3">
                {profile.data.photos.map((path, index) => (
                  <AuthenticatedImage
                    key={path}
                    path={path}
                    accessibilityLabel={`Profile photo ${index + 1}`}
                    width="100%"
                    aspectRatio={4 / 3}
                    objectFit="cover"
                    borderRadius="$sm"
                  />
                ))}
              </YStack>
            )}

            {/* Issue #216: prompts get real visual weight now -- each
                one is its own card, question in medium-large semibold
                and answer in the app's largest readable body size,
                closer to how Hinge/Bumble surface a prompt answer as
                real profile content instead of the previous small
                $colorTertiary label + plain Paragraph, which read as
                an afterthought (founder direction, #216: "prompts
                front and center, not an afterthought"). SolidSurface,
                not cardStyle -- 2026-09-16 design pass: the SAME
                content type (a profile's own prompt answers) already
                got this material on the editing side
                (profile/index.tsx's PromptSection), and a viewer
                looking at someone else's profile should see the same
                surface the owner edited it on, not a different one. */}
            {profile.data.prompts.length > 0 && (
              <YStack gap="$3">
                {profile.data.prompts.map((prompt) => (
                  <SolidSurface key={prompt.question} style={{ padding: space[4] }}>
                    <YStack gap="$2">
                      <Text color="$colorSecondary" fontSize="$meta" lineHeight="$meta" fontWeight="$semibold">
                        {prompt.question}
                      </Text>
                      {/* fontSize only, no lineHeight token -- see
                          components/heading.tsx's own doc comment:
                          fontSize/lineHeight aren't wired into this app's
                          Tamagui token scale, so a "$N" lineHeight silently
                          fails to resolve; every other real pairing in
                          this app uses raw numbers together or fontSize
                          alone, matching the safe, established pattern. */}
                      <Text color="$color" fontSize="$body" lineHeight="$body">
                        {prompt.answer}
                      </Text>
                    </YStack>
                  </SolidSurface>
                ))}
              </YStack>
            )}

            {profile.data.interests.length > 0 && (
              <XStack flexWrap="wrap" gap="$2">
                {profile.data.interests.map((interest) => (
                  <View
                    key={interest}
                    backgroundColor="$card"
                    borderRadius="$sm"
                    borderWidth={borderWidth.hairline}
                    borderColor="$borderColor"
                    paddingHorizontal="$3"
                    paddingVertical="$1"
                  >
                    <Text color="$colorSecondary" fontSize="$meta" lineHeight="$meta">
                      {interest}
                    </Text>
                  </View>
                ))}
              </XStack>
            )}

            {!isOwnProfile && (
              <YStack gap="$3" pt="$4">
                {/* Issue #100: the "Request reveal" button used to
                    render, tappable, before revealRequests (which is
                    what existingRequest is derived from) had actually
                    resolved -- a rapid tap in that window could fire a
                    duplicate createRevealRequest the backend then
                    rejected as a generic-looking 409, rather than the
                    button correctly never having offered a second
                    request in the first place. Mirrors the pattern this
                    screen already uses for profile.isPending above. */}
                {revealRequests.isPending ? (
                  <View alignItems="center" py="$4">
                    <LoadingIndicator />
                  </View>
                ) : existingRequest && !canReRequest ? (
                  <ProfileRevealStatus
                    request={existingRequest}
                    currentUserId={currentUserId as string}
                    otherUserId={userId as string}
                  />
                ) : (
                  <>
                    {/* Issue #151 AC4a: nothing anywhere near this button
                        ever explained what "reveal" actually does --
                        the only such copy in the app lived on the
                        unauthenticated Preview screen's marketing pitch,
                        never surfaced at the actual point of requesting
                        one. Kept to one line, plain language, matching
                        this app's voice elsewhere on this screen. */}
                    <Paragraph color="$colorSecondary" fontSize="$meta" lineHeight="$meta">
                      Reveal shares your real identity with this person -- only once you both agree. They&apos;ll get a
                      request to confirm before anything is shared.
                    </Paragraph>
                    {requestReveal.isError && (
                      <Paragraph color="$destructive" fontSize="$meta" lineHeight="$meta">
                        {requestReveal.error.message || 'Something went wrong. Check your connection and try again.'}
                      </Paragraph>
                    )}
                    {/* Issue #152 AC2: same paused-mutation messaging as
                        create-thread's own -- see that screen's doc
                        comment for why this is TanStack's own
                        networkMode: 'online' behavior, not a hand-rolled
                        queue. */}
                    {requestReveal.isPaused && (
                      <Paragraph color="$colorSecondary" fontSize="$meta" lineHeight="$meta">
                        You&apos;re offline. This will send as soon as you&apos;re back online.
                      </Paragraph>
                    )}
                    <PrimaryButton
                      loading={requestReveal.isPending && !requestReveal.isPaused}
                      onPress={() => {
                        if (!requestReveal.isPending) requestReveal.mutate();
                      }}
                    >
                      {requestReveal.isPaused ? (
                        'Waiting to reconnect...'
                      ) : requestReveal.isPending ? (
                        <ActivityIndicator color="white" />
                      ) : (
                        'Request reveal'
                      )}
                    </PrimaryButton>
                  </>
                )}

                <XStack gap="$4" pt="$2">
                  <TextLink
                    fontSize="$meta"
                    lineHeight="$meta"
                    onPress={() => router.push({ pathname: '/block/[userId]', params: { userId: userId as string } })}
                  >
                    Block
                  </TextLink>
                  <TextLink
                    fontSize="$meta"
                    lineHeight="$meta"
                    onPress={() =>
                      router.push({
                        pathname: '/report/[targetType]/[targetId]',
                        params: { targetType: 'user', targetId: userId as string },
                      })
                    }
                  >
                    Report
                  </TextLink>
                </XStack>
              </YStack>
            )}
          </ScrollView>
        ) : null}
      </YStack>
    </ScreenContainer>
  );
}

function ProfileRevealStatus({
  request,
  currentUserId,
  otherUserId,
}: {
  request: RevealRequest;
  currentUserId: string;
  otherUserId: string;
}) {
  const router = useRouter();
  const description = describeRevealRequestStatus(request, currentUserId);

  // A real "Message" action for a mutual connection -- a user hit the
  // old copy's stale "Direct messaging isn't here yet" claim live in
  // production after #64 shipped (see reveal-request-status.ts's own
  // updated comment). Only fetched once mutual (`enabled`), same
  // reasoning as revealRequests above -- this screen has no other need
  // for the connections list. Same ['me', 'connections'] queryKey
  // messages/index.tsx and the Matches tab badge already use, so
  // TanStack dedupes into one request wherever more than one of these
  // is mounted.
  const connections = useAllConnections({ enabled: request.status === 'mutual' });
  const connection = connections.data?.find((c) => c.otherUserId === otherUserId);

  // Mirrors reveal-requests's own DeclinedRequestRow -- only meaningful
  // for the original requester (the only side the backend's cooldown
  // ever gates); undefined for the recipient side or once elapsed.
  const cooldown =
    request.status === 'declined' && request.requesterId === currentUserId
      ? describeCooldown(new Date(), request.cooldownUntil)
      : undefined;

  return (
    <SolidSurface style={{ padding: space[4] }}>
      <YStack gap="$3">
        <YStack gap="$2">
          <Badge variant={description.variant}>{description.label}</Badge>
          <Text color="$colorSecondary" fontSize="$meta" lineHeight="$meta">
            {description.detail}
          </Text>
          {cooldown && (
            <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
              {cooldown}
            </Text>
          )}
        </YStack>
        {connection && (
          <PrimaryButton
            size="sm"
            alignSelf="flex-start"
            onPress={() =>
              router.push({ pathname: '/messages/[connectionId]', params: { connectionId: connection.id } })
            }
          >
            Message
          </PrimaryButton>
        )}
      </YStack>
    </SolidSurface>
  );
}
